import { useEffect, useState } from 'react';

/**
 * Lädt das PayPal-JS-SDK als Script-Tag (Marktplatz-Rework T6, Plan 3.5):
 * `client-id=<Partner>&merchant-id=<Seller>&currency=EUR&intent=capture`. Im
 * Multiparty-Modus erzeugt das SDK die Buttons im Namen des Verkäufers
 * (`merchant-id`), die Plattformgebühr hängt an der serverseitig erzeugten
 * Order. Kein Popup-Polling — Genehmigung und Capture laufen über die
 * SDK-Callbacks.
 *
 * Das SDK ist an seine URL-Parameter gebunden: ändern sich Client-ID,
 * Merchant-ID oder Währung, wird das alte Script samt `window.paypal`
 * entfernt und neu geladen.
 */

export interface PayPalButtonsActions {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

export interface PayPalButtonsOptions {
    style?: Record<string, unknown>;
    createOrder: () => Promise<string>;
    onApprove: (data: { orderID: string }) => Promise<void> | void;
    onCancel?: () => void;
    onError?: (err: unknown) => void;
}

export interface PayPalNamespace {
    Buttons: (options: PayPalButtonsOptions) => {
        render: (container: HTMLElement | string) => Promise<void>;
        close?: () => Promise<void>;
    };
}

declare global {
    interface Window {
        paypal?: PayPalNamespace;
    }
}

export interface PayPalSdkParams {
    clientId: string | null | undefined;
    merchantId?: string | null;
    currency?: string | null;
    partnerAttributionId?: string | null;
    /** Nur laden, wenn tatsächlich bezahlt wird (> 0 €, Herkunft öffentlich). */
    enabled: boolean;
}

export interface PayPalSdkState {
    ready: boolean;
    error: string | null;
    paypal: PayPalNamespace | null;
}

const SDK_ORIGIN = 'https://www.paypal.com/sdk/js';
const SCRIPT_ATTR = 'data-oa-paypal-sdk';

export const buildPayPalSdkUrl = ({ clientId, merchantId, currency }: PayPalSdkParams): string => {
    const query = new URLSearchParams();
    query.set('client-id', clientId ?? '');
    if (merchantId) query.set('merchant-id', merchantId);
    query.set('currency', currency || 'EUR');
    query.set('intent', 'capture');
    query.set('components', 'buttons');
    return `${SDK_ORIGIN}?${query.toString()}`;
};

export function usePayPalSdk(params: PayPalSdkParams): PayPalSdkState {
    const { clientId, merchantId, currency, partnerAttributionId, enabled } = params;
    const [state, setState] = useState<PayPalSdkState>({ ready: false, error: null, paypal: null });

    useEffect(() => {
        if (!enabled || !clientId) {
            setState({ ready: false, error: null, paypal: null });
            return undefined;
        }

        const url = buildPayPalSdkUrl({ clientId, merchantId, currency, enabled });
        const existing = document.querySelector<HTMLScriptElement>(`script[${SCRIPT_ATTR}]`);

        if (existing && existing.getAttribute(SCRIPT_ATTR) === url && window.paypal) {
            setState({ ready: true, error: null, paypal: window.paypal });
            return undefined;
        }
        if (existing) {
            existing.remove();
            delete window.paypal;
        }

        let cancelled = false;
        setState({ ready: false, error: null, paypal: null });

        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.setAttribute(SCRIPT_ATTR, url);
        if (partnerAttributionId) script.setAttribute('data-partner-attribution-id', partnerAttributionId);
        script.onload = () => {
            if (cancelled) return;
            if (window.paypal) setState({ ready: true, error: null, paypal: window.paypal });
            else setState({ ready: false, error: 'sdk_unavailable', paypal: null });
        };
        script.onerror = () => {
            if (!cancelled) setState({ ready: false, error: 'sdk_load_failed', paypal: null });
        };
        document.head.appendChild(script);

        return () => {
            cancelled = true;
        };
    }, [enabled, clientId, merchantId, currency, partnerAttributionId]);

    return state;
}

export default usePayPalSdk;
